import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, Code, Zap, Layers, Terminal, HardDrive, Cpu } from 'lucide-react';
import { editorAPI } from '@/lib/editor/api';
import { useAuth } from '@/contexts/AuthContext';
import { getActiveWorkspaceId } from '@/stores/workspace-store';

export default function NewProjectFull() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    templateId: '',
    mode: 'lite' as 'lite' | 'power',
  });

  const templates = [
    { id: '', name: 'Blank Project', description: 'Start with an empty project' },
    { id: 'react', name: 'React App', description: 'Create React app with TypeScript' },
    { id: 'nextjs', name: 'Next.js App', description: 'Full-stack Next.js application' },
    { id: 'node', name: 'Node.js API', description: 'Express.js REST API' },
    { id: 'html', name: 'HTML/CSS/JS', description: 'Simple web page' },
  ];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      alert('Please enter a project name');
      return;
    }

    setLoading(true);
    try {
      const project = await editorAPI.createProject({
        name: formData.name,
        description: formData.description,
        templateId: formData.templateId,
        mode: formData.mode,
      });
      
      const wsId = getActiveWorkspaceId();
      navigate(`/dashboard/editor/${project.id}${wsId ? `?ws=${wsId}` : ''}`);
    } catch (error) {
      console.error('Failed to create project:', error);
      alert('Failed to create project. Please try again.');
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background px-4 py-8">
      <div className="w-full max-w-2xl">
        <Link
          to="/dashboard/editor"
          className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground mb-6"
        >
          <ArrowLeft size={20} />
          Back to Projects
        </Link>

        <div className="bg-card rounded-lg border border-border p-8 shadow-xl">
        <div className="flex items-center gap-3 mb-6">
          <div className="p-2 bg-purple-100 dark:bg-purple-900/30 rounded-lg">
            <Code size={32} className="text-purple-600 dark:text-purple-400" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-foreground">
              Create New Project
            </h1>
            <p className="text-muted-foreground">
              Set up a new code project
            </p>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Mode Selection */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-3">
              Development Mode
            </label>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Lite Mode */}
              <button
                type="button"
                onClick={() => setFormData({ ...formData, mode: 'lite' })}
                className={`relative p-4 border-2 rounded-lg text-left transition-all ${
                  formData.mode === 'lite'
                    ? 'border-purple-500 bg-purple-500/10'
                    : 'border-border hover:border-muted-foreground bg-card'
                }`}
              >
                {formData.mode === 'lite' && (
                  <div className="absolute top-2 right-2 w-6 h-6 bg-purple-500 rounded-full flex items-center justify-center">
                    <svg className="w-4 h-4 text-primary-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                )}
                <div className="flex items-center gap-3 mb-3">
                  <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
                    <Zap className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-foreground">Lite Mode</h3>
                    <p className="text-xs text-muted-foreground">Browser-based editor</p>
                  </div>
                </div>
                <ul className="space-y-1 text-sm text-muted-foreground">
                  <li className="flex items-center gap-2">
                    <svg className="w-4 h-4 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    Instant access
                  </li>
                  <li className="flex items-center gap-2">
                    <svg className="w-4 h-4 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    No setup required
                  </li>
                  <li className="flex items-center gap-2">
                    <svg className="w-4 h-4 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    Basic editing features
                  </li>
                </ul>
              </button>

              {/* Power Mode */}
              <button
                type="button"
                onClick={() => setFormData({ ...formData, mode: 'power' })}
                className={`relative p-4 border-2 rounded-lg text-left transition-all ${
                  formData.mode === 'power'
                    ? 'border-purple-500 bg-purple-500/10'
                    : 'border-border hover:border-muted-foreground bg-card'
                }`}
              >
                {formData.mode === 'power' && (
                  <div className="absolute top-2 right-2 w-6 h-6 bg-purple-500 rounded-full flex items-center justify-center">
                    <svg className="w-4 h-4 text-primary-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                )}
                <div className="flex items-center gap-3 mb-3">
                  <div className="p-2 bg-purple-100 dark:bg-purple-900/30 rounded-lg">
                    <Layers className="w-5 h-5 text-purple-600 dark:text-purple-400" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-foreground">Power Mode</h3>
                    <p className="text-xs text-muted-foreground">Full VS Code environment</p>
                  </div>
                </div>
                <ul className="space-y-1 text-sm text-muted-foreground">
                  <li className="flex items-center gap-2">
                    <Terminal className="w-4 h-4 text-purple-500" />
                    Terminal access
                  </li>
                  <li className="flex items-center gap-2">
                    <Cpu className="w-4 h-4 text-purple-500" />
                    2 OCPU + 4GB RAM
                  </li>
                  <li className="flex items-center gap-2">
                    <HardDrive className="w-4 h-4 text-purple-500" />
                    32GB persistent storage
                  </li>
                </ul>
                <div className="mt-2 pt-2 border-t border-border">
                  <p className="text-xs text-amber-600 dark:text-amber-400">
                    Container will be provisioned
                  </p>
                </div>
              </button>
            </div>
          </div>

          <div>
            <label htmlFor="name" className="block text-sm font-medium text-foreground mb-2">
              Project Name *
            </label>
            <input
              type="text"
              id="name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="w-full px-3 py-2 border border-input rounded-lg bg-background text-foreground focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              placeholder="My Awesome Project"
              required
            />
          </div>

          <div>
            <label htmlFor="description" className="block text-sm font-medium text-foreground mb-2">
              Description
            </label>
            <textarea
              id="description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              className="w-full px-3 py-2 border border-input rounded-lg bg-background text-foreground focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              placeholder="Brief description of your project"
              rows={3}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Template
            </label>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {templates.map((template) => (
                <button
                  key={template.id}
                  type="button"
                  onClick={() => setFormData({ ...formData, templateId: template.id })}
                  className={`p-3 border rounded-lg text-left transition-colors ${
                    formData.templateId === template.id
                      ? 'border-purple-500 bg-purple-500/10'
                      : 'border-border hover:bg-accent bg-card'
                  }`}
                >
                  <div className="font-medium text-foreground">
                    {template.name}
                  </div>
                  <div className="text-sm text-muted-foreground mt-1">
                    {template.description}
                  </div>
                </button>
              ))}
            </div>
          </div>

          <div className="flex gap-3">
            <button
              type="submit"
              disabled={loading}
              className="flex-1 py-2 px-4 bg-purple-600 hover:bg-purple-700 disabled:bg-purple-400 text-primary-foreground rounded-lg transition-colors"
            >
              {loading ? 'Creating...' : 'Create Project'}
            </button>
            <Link
              to="/dashboard/editor"
              className="py-2 px-4 border border-border hover:bg-accent rounded-lg transition-colors"
            >
              Cancel
            </Link>
          </div>
        </form>
        </div>
      </div>
    </div>
  );
}